import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, InputAdornment, Paper, TextField, Tooltip, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { notify } from '../notifications';
import { apiErrorMessage } from '../admin/reports/edit/editApi';
import { fetchStaffOverview } from './staffApi';
import { IssuedPassword, StaffCreated, StaffCreateInput, StaffMember, StaffOverview, StaffTarget } from './types';
import { filterStaff, limitReached } from './staffUtils';
import { primaryButtonSx, searchInputSx, toolbarSx } from './staffStyles';
import StaffTable from './StaffTable';
import StaffCreateDialog from './StaffCreateDialog';
import StaffDetailDrawer from './StaffDetailDrawer';
import StaffPasswordDialog from './StaffPasswordDialog';

interface StaffSectionProps {
  /** point - кабинет точки (свои подчинённые), admin - карточка точки в «Пользователях» */
  mode: 'point' | 'admin';
  /** Точка в админке */
  masterId?: number;
}

/**
 * Сотрудники точки (PROJECT_NOTES §44): список, создание и карточка. Что показывать, решает сервер
 * флагами operator; лимит считает только активных.
 */
const StaffSection: React.FC<StaffSectionProps> = ({ mode, masterId }) => {
  const target = useMemo<StaffTarget>(
    () => (mode === 'admin' ? { mode: 'admin', masterId: masterId || 0 } : { mode: 'point' }),
    [mode, masterId]
  );
  const [overview, setOverview] = useState<StaffOverview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [issued, setIssued] = useState<IssuedPassword | null>(null);

  // Повторная загрузка после действий не прячет список за индикатором
  const load = useCallback(async () => {
    try {
      setOverview(await fetchStaffOverview(target));
      setLoadError(null);
    } catch (err) {
      setLoadError(apiErrorMessage(err, 'Не удалось загрузить сотрудников'));
    }
  }, [target]);

  useEffect(() => {
    setOverview(null);
    setOpenId(null);
    load();
  }, [load]);

  const rows = useMemo(() => (overview ? filterStaff(overview.staff, query) : []), [overview, query]);
  const openMember = useMemo<StaffMember | null>(() => {
    if (!overview || openId === null) return null;
    const found = overview.staff.filter(member => member.id === openId);
    return found.length > 0 ? found[0] : null;
  }, [overview, openId]);

  const handleSelect = useCallback((member: StaffMember) => setOpenId(member.id), []);

  const handleCreated = (created: StaffCreated, input: StaffCreateInput) => {
    setCreateOpen(false);
    if (created.password) {
      setIssued({ login: created.login, fullName: input.fullName, password: created.password, mustChange: input.kind === 'person' });
    } else {
      notify.success(`Сотрудник ${created.login} создан`);
    }
    load();
  };

  if (!overview) {
    return loadError ? (
      <Alert
        severity="error"
        sx={{ borderRadius: '12px' }}
        action={
          <Button color="inherit" size="small" onClick={load} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Повторить
          </Button>
        }
      >
        {loadError}
      </Alert>
    ) : (
      <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  const { point, operator } = overview;
  const full = limitReached(overview.active, overview.limit);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {point.blocked && (
        <Alert severity="warning" sx={{ borderRadius: '12px' }}>
          Точка заблокирована: её сотрудники не могут войти в портал, создание и разблокировка сотрудников недоступны
        </Alert>
      )}
      {loadError && (
        <Alert severity="error" sx={{ borderRadius: '12px' }}>{loadError}</Alert>
      )}

      <Paper elevation={0} sx={toolbarSx}>
        <TextField
          size="small"
          placeholder="Поиск по имени или логину"
          value={query}
          onChange={e => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
              </InputAdornment>
            ),
            sx: searchInputSx
          }}
          sx={{ flexGrow: 1, minWidth: { xs: '100%', sm: 240 } }}
        />

        <Tooltip title="Заблокированные сотрудники не занимают место в лимите" arrow>
          <Typography variant="body2" sx={{ color: full ? '#c2410c' : '#64748b', fontWeight: 600, whiteSpace: 'nowrap', px: 0.5 }}>
            Активных: {overview.active} из {overview.limit}
          </Typography>
        </Tooltip>

        {operator.canCreate && !point.blocked && (
          <Tooltip title={full ? 'Достигнут лимит активных сотрудников: заблокируйте неиспользуемые учётки' : ''} arrow>
            <span>
              <Button
                variant="contained"
                disableElevation
                startIcon={<PersonAddIcon sx={{ fontSize: 18 }} />}
                onClick={() => setCreateOpen(true)}
                disabled={full}
                sx={primaryButtonSx}
              >
                Добавить сотрудника
              </Button>
            </span>
          </Tooltip>
        )}
      </Paper>

      <StaffTable rows={rows} tree={overview.tree} empty={overview.staff.length === 0} onSelect={handleSelect} />

      {operator.canCreate && (
        <StaffCreateDialog
          open={createOpen}
          target={target}
          point={point}
          tree={overview.tree}
          onClose={() => setCreateOpen(false)}
          onCreated={handleCreated}
        />
      )}

      <StaffDetailDrawer
        target={target}
        member={openMember}
        point={point}
        tree={overview.tree}
        operator={operator}
        limitFull={full}
        onIssued={setIssued}
        onChanged={load}
        onClose={() => setOpenId(null)}
      />

      <StaffPasswordDialog issued={issued} onClose={() => setIssued(null)} />
    </Box>
  );
};

export default StaffSection;
